import Vue from 'vue' // 可观察状态供确认页、答题页和控制台共享。
import { experienceRequest, operationToken, saveOperation, experienceDevice, experienceCacheKey } from '@/api/examExperience' // 所有网络调用统一附加当前会话凭证。

export const experienceState = Vue.observable({
  enabled: false, // 先检测后端能力，不向旧Gateway发送未知类型。
  canUse: false, // 默认隐藏菜单，资格只接受服务器当前响应，绝不从缓存恢复。
  ready: false, // 首次导航等任务恢复完成后再放行。
  control: null, // 电脑组的自由练习权限独立于场次，交卷后仍然有效。
  task: null, // 默认无任务，添加列表本身不会触发跳转。
  offline: false, // 网络失败保留任务，并显示同步状态。
  serverOffset: 0 // 保留服务端和本机时间差供状态观察。
})
const listeners = new Set() // 页面可以订阅并在卸载时移除，不覆盖扫码登录回调。
let scopeLogin = localStorage.getItem('token') // 首次刷新保留同一登录的操作令牌，资格失败或随后换账号才清除。
let router = null // 启动时注入，避免路由与业务模块互相导入。
let socket = null // 复用已有WebSocket连接。
let pending = null // 合并密集事件引发的快照请求。
let bootPromise = null // 首次导航等待能力检查与任务恢复。
let submitPromise = null // 自动交卷与重复通知共用请求锁。
let enabledKnown = false // 旧后端/未执行SQL时不发送新消息类型。
let refreshAgain = false // 请求期间收到新通知时，当前响应可能已过时，需要再读一次。
let serverBase = Date.now() / 1000 // 最近一次服务器时间校准点。
let monotonicBase = performance.now() // 单调时钟不受操作系统改时间影响。

export function subscribeExperience(callback) {
  listeners.add(callback) // 多页面独立监听。
  return () => listeners.delete(callback) // 防止页面卸载后回调残留。
}

export function applyExperienceEligibility(data) {
  experienceState.canUse = data.can_use === true // 功能已部署不代表当前手机号已获准。
  if (!experienceState.canUse) saveOperation('') // 撤权或旧后端没有资格字段时清除本机旧操作令牌。
}

function syncExperienceLogin() {
  const login = localStorage.getItem('token') // 登录是缓存和菜单资格的共同边界。
  if (scopeLogin === login) return
  scopeLogin = login; applyExperienceEligibility({}) // 新账号必须重新从后端获得资格。
  experienceState.control = null; experienceState.task = null // 不能沿用上一账号的路由限制或入场状态。
}

function deniedMenu(to) {
  return !experienceState.canUse && to.path.toLowerCase() === '/myerror' && Number(to.query.ix) === 7 // 手工输入个人中心参数也不能挂载受保护面板。
}

function emit() {
  listeners.forEach(callback => { try { callback(experienceState.task) } catch (error) { console.error('考试体验状态监听失败', error) } }) // 一个页面异常不能阻断其他页面。
}

export function experienceNow() {
  return Math.floor(serverBase + (performance.now() - monotonicBase) / 1000) // 两端按服务端校准后的单调时间计时。
}

export function experienceConfirmRoute(task = experienceState.task, control = experienceState.control) {
  const query = { exam_type: 'zhenshi', exam_modes: 'zhenshi', rule_type: '[]' } // 沿用原全真考试入口参数。
  if (task) query.experience_task = String(task.id) // 场次标记只定位页面，不能授予入场权限。
  if (control && (!task || !['waiting', 'running', 'stopping'].includes(task.status))) query.experience_waiting = '1' // 显式区分已回到等待页，刷新不再重复展示旧成绩。
  if (control) query.experience_target = String(control.target_id) // 无活动任务时仍能识别受控等待页。
  return { path: '/exam-confirm', query } // 成绩页和路由守卫共用同一返回目标。
}

function destination(task) {
  const control = experienceState.control // 仅使用后端鉴权后的电脑组权限。
  if (task && ['running', 'stopping'].includes(task.status)) return { ...experienceConfirmRoute(task), path: task.admitted ? '/exam' : '/exam-confirm' } // 正在考试时即使允许自由练习，也不能离开本场。
  if ((task && task.status === 'waiting' && (!control || !control.allow_free_practice)) || (control && !control.allow_free_practice)) return experienceConfirmRoute(task) // 关闭权限后，未开始和已交卷阶段都留在确认页。
  return null // 原普通用户和允许自由练习的待考账号保持正常导航。
}

function matches(to, target) {
  return to.path.toLowerCase() === target.path && Object.keys(target.query).every(key => String(to.query[key]) === target.query[key]) // 防止换URL参数偷偷修改考场配置。
}

function ownResult(to, task) {
  return !!(task && task.status === 'completed' && task.is_participant && Number(task.paper_id) > 0 && to.path.toLowerCase() === '/examresult' && Number(to.query.paper_id) === Number(task.paper_id)) // 限制访问时仅放行本场考生自己的成绩页。
}

function staleExam(to, task) {
  return to.path.toLowerCase() === '/exam' && to.query.experience_task && (!task || String(to.query.experience_task) !== String(task.id) || !['running', 'stopping'].includes(task.status) || !task.admitted) // 已结束场次不能因权限放开而落入普通取题逻辑。
}

function marker(kind, task) {
  return 'pc-experience:' + kind + ':' + task.id // 每次主控重新开始都会生成新场次，标记不会阻止新场次跳转。
}

function enforce(previousTask) {
  if (!router || !experienceState.enabled || !localStorage.getItem('token')) return // 未登录时交由原登录流程恢复。
  const task = experienceState.task; const control = experienceState.control; const current = router.currentRoute // 同一次快照内使用一致的数据。
  if (task && ['completed', 'cancelled'].includes(task.status)) {
    const wasActive = previousTask && Number(previousTask.id) === Number(task.id) && ['waiting', 'running', 'stopping'].includes(previousTask.status) // 只处理刚结束的本场。
    const inTask = !current.query.experience_waiting && ['/exam', '/exam-confirm'].includes(current.path.toLowerCase()) && String(current.query.experience_task || '') === String(task.id) // 刷新仍能恢复已经后台结算的场次。
    if ((wasActive || inTask) && !sessionStorage.getItem(marker('finished', task))) {
      sessionStorage.setItem(marker('finished', task), '1') // 先标记再导航，避免轮询把已返回的用户反复送回成绩页。
      localStorage.removeItem(experienceCacheKey(task.id)) // 仅清理本场已交卷答案。
      const result = task.paper_id && task.is_participant ? { path: '/examresult', query: { paper_id: task.paper_id, result_car_type_id: task.car_type_id, result_subject_id: task.subject_id, experience_task: String(task.id) } } : control ? experienceConfirmRoute(task) : { path: '/' } // 交卷先看成绩；未入场取消只回等待页，不生成空成绩。
      router.replace(result).catch(() => {}) // 结果页由守卫单独放行，关闭自由练习也能看本次成绩。
      return // 不能马上再被等待页覆盖刚打开的成绩页。
    }
  }
  if (task && task.status === 'waiting' && !sessionStorage.getItem(marker('prompted', task))) {
    sessionStorage.setItem(marker('prompted', task), '1') // 一次开始只主动提示一次，允许自由练习后不会被轮询反复拉回。
    router.replace(experienceConfirmRoute(task)).catch(() => {}) // 无论当前是否允许自由练习，新的开始指令都先跳到确认页。
  } else {
    const target = destination(task) // 无新开始指令时只执行持续的访问权限。
    if (!ownResult(current, task) && target && !matches(current, target)) router.replace(target).catch(() => {}) // 返回键、刷新和主控关闭权限都恢复正确考场。
    else if (staleExam(current, task)) router.replace(control ? experienceConfirmRoute(task) : { path: '/' }).catch(() => {}) // 老书签不能再次取题。
    else if (deniedMenu(current)) router.replace({ path: '/myerror', query: { ...current.query, ix: 4 } }).catch(() => {}) // 撤销资格后离开主控页，其他个人中心菜单继续可用。
    else if (!control && current.path.toLowerCase() === '/exam-confirm' && current.query.experience_target && !target) router.replace({ path: '/' }).catch(() => {}) // 主控移除关系后及时解除等待页。
  }
  if (task && task.status === 'stopping' && task.admitted) autoSubmitExperience(task.id).catch(() => {}) // 主控退出时无论目标停在哪个页面都自动交卷。
}

function cacheSnapshot(restore = false) {
  try {
    const key = 'pc-experience:scope'; const login = localStorage.getItem('token') // 缓存严格绑定当前登录，不能串到其他账号。
    if (restore) {
      const saved = JSON.parse(sessionStorage.getItem(key) || 'null') // 硬刷新断网时保留上次已确认的限制。
      if (saved && login && saved.login === login) { experienceState.control = saved.control; experienceState.task = saved.task; experienceState.enabled = true } // 网络失败不会自动放开已知权限。
    } else if (experienceState.enabled && login) sessionStorage.setItem(key, JSON.stringify({ login, control: experienceState.control, task: experienceState.task })) // 只缓存context白名单，没有试题、密码或确认码。
    else sessionStorage.removeItem(key) // 未登录或功能关闭时不保留旧范围。
  } catch (error) { console.warn('考试体验状态缓存不可用') } // 浏览器存储失败不能中断交卷与服务器同步。
}

export function bindExperienceSocket() {
  syncExperienceLogin() // 重连或心跳遇到换账号时先丢弃旧授权。
  if (!enabledKnown || !experienceState.enabled || !socket || !localStorage.getItem('token')) return // 能力确认之前不向旧Gateway发送未知类型。
  socket.send({ type: 'exam_experience.subscribe', platforms: 'pc', access_token: localStorage.getItem('token'), operation: operationToken(), device: experienceDevice() }) // 不发送手机号或任意分组名。
}

export function refreshExperience() {
  syncExperienceLogin() // 轮询不能在新登录中暴露上一用户的菜单。
  if (!experienceState.enabled || !localStorage.getItem('token')) return Promise.resolve(null) // 普通未登录页面不调用受保护接口。
  if (pending) { refreshAgain = true; return pending } // 当前响应返回后再补读，避免开始和退出紧邻时漏掉后者。
  const login = localStorage.getItem('token') // 请求返回前切换账号时，不能把上一用户的资格赋给新登录。
  pending = experienceRequest('context').then(data => {
    if (login !== localStorage.getItem('token')) { applyExperienceEligibility({}); refreshAgain = true; return null } // 旧响应丢弃，随后读取新身份。
    applyExperienceEligibility(data) // 推送和低频恢复都更新菜单资格。
    const previous = experienceState.task // 保存旧状态以判断是否刚完成。
    experienceState.serverOffset = Number(data.server_time) * 1000 - Date.now() // 校准服务器时间，不用本机系统时间决定交卷。
    serverBase = Number(data.server_time); monotonicBase = performance.now() // 用单调时钟持续计时，本机改时间不影响显示。
    experienceState.control = data.control || null // 成功快照才更新权限，断线时继续保留已知限制。
    experienceState.task = data.task // 只接受后端返回的白名单字段。
    experienceState.offline = false // 快照读取成功后解除通信提示。
    if (data.task) sessionStorage.setItem('pc-experience:active', data.task.id) // 后续请求携带本场入场凭证。
    cacheSnapshot() // 刷新页面后仍可恢复已确认的电脑组范围。
    emit() // 先让答题页保存/禁用交互，再执行路由和自动交卷。
    Promise.resolve().then(() => enforce(previous)) // 不等待路由，避免守卫等待当前请求造成循环死锁。
    return data.task // 页面需要时可等待本次同步。
  }).catch(error => {
    experienceState.offline = true // 网络失败不能清空已知的活动任务或重置计时。
    emit() // 答题页展示保存状态，控制台禁止盲目操作。
    throw error // 调用方决定是否提示，后台恢复不刷屏。
  }).finally(() => { pending = null; if (refreshAgain) { refreshAgain = false; refreshExperience().catch(() => {}) } }) // 合并后仍补齐请求期间到达的新事件。
  return pending
}

export function autoSubmitExperience(taskId) {
  if (submitPromise) return submitPromise // 重复退出消息和页面计时器不会重复请求。
  let questions = [] // 即使零答题也必须完成自动交卷。
  try { questions = JSON.parse(localStorage.getItem(experienceCacheKey(taskId)) || '[]') } catch (error) { questions = [] } // 缓存损坏时由服务器保存答案兜底。
  submitPromise = experienceRequest('submit', { task_id: taskId, questions }).then(() => refreshExperience()).finally(() => { submitPromise = null }) // 后台结算也会发送结果事件。
  return submitPromise
}

/** 在Vue挂载前安装全局运行时：任意页面都能接收考试指令。 */
export function initExamExperience(appRouter, appSocket) {
  router = appRouter; socket = appSocket // 注入项目原有路由与连接。
  syncExperienceLogin() // 初始状态与当前普通登录绑定，再恢复该登录的任务缓存。
  const bootLogin = localStorage.getItem('token') // 首次能力请求也绑定当时的普通登录。
  cacheSnapshot(true) // 服务器读取失败时沿用同一登录的最后状态，读取成功后立即覆盖。
  bootPromise = localStorage.getItem('token') ? experienceRequest('status').then(data => {
    if (bootLogin !== localStorage.getItem('token')) return // 防止迟到响应复用上一登录资格。
    applyExperienceEligibility(data) // 首次确认前不闪现新菜单。
    experienceState.enabled = data.enabled === true // 缺表时只关闭本功能，不影响原菜单和考试。
    if (!experienceState.enabled) { experienceState.control = null; experienceState.task = null; cacheSnapshot() } // 旧后端明确关闭能力时清理本功能缓存。
    enabledKnown = true // 能力检查完成后才允许订阅。
    bindExperienceSocket() // 在线连接立即认证，不等15秒心跳。
    return refreshExperience() // 初始路由渲染前恢复正在进行的任务。
  }).catch(() => { experienceState.offline = true }).finally(() => { experienceState.ready = true }) : Promise.resolve().then(() => { experienceState.ready = true }) // 旧登录页照常显示。
  router.beforeEach(async (to, from, next) => {
    syncExperienceLogin() // 手工导航时也立即隔离切换后的账号。
    await bootPromise // 初次导航等待服务器确认任务。
    const task = experienceState.task; const target = destination(task) // 电脑组限制在交卷后继续生效。
    if (localStorage.getItem('token') && target && !ownResult(to, task) && !matches(to, target)) next({ ...target, replace: true }) // 返回和手输地址都恢复正确考场。
    else if (localStorage.getItem('token') && staleExam(to, task)) next({ ...(experienceState.control ? experienceConfirmRoute(task) : { path: '/' }), replace: true }) // 禁止重放过期受控考试URL。
    else if (deniedMenu(to)) next({ path: '/myerror', query: { ...to.query, ix: 4 }, replace: true }) // 非名单用户不能直接打开控制台URL。
    else next() // 无任务或合法目标保持旧路由行为。
  })
  socket.subscribe(data => {
    if (data.type === 'connect') bindExperienceSocket() // 重连后client_id变化，重新认证和分组。
    if (data.type === 'heartbeat') bindExperienceSocket() // 及时发现账号切换和操作授权过期。
    if (data.type === 'exam_experience.subscribed' && data.authenticated) {
      refreshExperience().catch(() => {}) // 订阅成功补拉遗漏的开始/退出指令。
      window.dispatchEvent(new CustomEvent('exam-experience-control-change')) // 主控重连时立即补读列表的在线状态。
    }
    if (data.type === 'exam_experience.presence') window.dispatchEvent(new CustomEvent('exam-experience-control-change')) // 上下线只刷新受保护主控列表，不触发考试导航。
    if (data.type === 'exam_experience.changed') {
      refreshExperience().catch(() => {}) // 推送只提示变化，真实数据仍由后端鉴权。
      window.dispatchEvent(new CustomEvent('exam-experience-control-change')) // 控制台单独刷新受保护列表和授权状态。
    }
  })
  const recover = () => {
    if (!experienceState.ready || !localStorage.getItem('token')) return // 未登录不做能力恢复请求。
    if (!enabledKnown || !experienceState.enabled) {
      const login = localStorage.getItem('token') // 恢复请求同样禁止跨登录回填资格。
      experienceRequest('status').then(data => { if (login !== localStorage.getItem('token')) return; applyExperienceEligibility(data); enabledKnown = true; experienceState.enabled = data.enabled === true; if (experienceState.enabled) { bindExperienceSocket(); refreshExperience().catch(() => {}) } }).catch(() => {}) // 首次网络失败或SQL稍后就绪时自动恢复。
    } else { bindExperienceSocket(); refreshExperience().catch(() => {}) } // 恢复网络和切回窗口时补齐任务。
  }
  window.addEventListener('exam-experience-eligibility-denied', () => { applyExperienceEligibility({}); recover() }) // 接口发现撤权时立即隐藏菜单，再恢复最后交卷状态。
  window.addEventListener('exam-experience-access-denied', recover) // 旧页面并发请求被权限拦截时主动更新路由。
  window.addEventListener('online', recover) // 离线期间服务端可能已经自动交卷。
  window.addEventListener('focus', recover) // 睡眠恢复时重新校准服务器计时。
  window.setInterval(recover, 15000) // 低频兜底，不依赖某条WebSocket推送一定送达。
}
